/**
 * cas-2be4 (P1.4 of the 2026-09-08 auth audit) — the ONE public-audit
 * exemption predicate.
 *
 * `/audit/<postId>?s=<token>` is the page Gabber texts customers. cas-3ff1
 * established that it is public and that a background 401 there is harmless:
 * nothing on it may tear down the session, probe/repair auth state, or bounce
 * the visitor. Until this module every plugin and the auth store decided that
 * with its own `window.location.pathname.startsWith('/audit')`, which
 *
 *   - also matched `/audits` (the signed-in archive), silently disabling the
 *     401 teardown there, and
 *   - missed the i18n mirror (`strategy: 'prefix_except_default'` serves the
 *     same page at `/es/audit/<postId>`), so a Spanish-locale visitor got the
 *     session-expired bounce the exemption exists to prevent.
 *
 * Pure module: no Nuxt, no window. Callers pass the pathname they have.
 */

/**
 * Non-default locale codes from nuxt.config.ts `i18n.locales` (the default
 * locale is served unprefixed). A test pins this list to the config so a new
 * locale cannot be added without the predicate learning it.
 */
export const NON_DEFAULT_LOCALE_CODES: readonly string[] = Object.freeze(['es']);

/** `/es/audit/x` → `/audit/x`; `/es` → `/`; anything else unchanged. */
export function stripLocalePrefix(path: string): string {
  for (const code of NON_DEFAULT_LOCALE_CODES) {
    const prefix = `/${code}`;
    if (path === prefix || path === `${prefix}/`) return '/';
    if (path.startsWith(`${prefix}/`)) return path.slice(prefix.length);
  }
  return path;
}

/**
 * True only on the texted public audit surface: `/audit` or `/audit/<...>`,
 * with or without a locale prefix, query or hash. `/audits` (archive),
 * `/p/<slug>/audits/<id>` (project audit detail) and everything else are
 * false — those pages are signed-in surfaces and keep normal auth handling.
 */
export function isPublicAuditPath(path: string | null | undefined): boolean {
  if (!path || path.charAt(0) !== '/') return false;
  const cut = path.search(/[?#]/);
  const pathname = stripLocalePrefix(cut === -1 ? path : path.slice(0, cut));
  return pathname === '/audit' || pathname.startsWith('/audit/');
}
